import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	Paper,
	Snackbar,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/Header/Header.jsx";
import JsonSchemaBox from "../../components/Common/JsonSchemaBox.jsx";
import { tokens } from "../../theme.js";
import { useUserContext } from "../../contexts/userContext.js";
import {
	COVERAGE_LIST_TYPES,
	getCoverageTableConfig,
	isAddMode,
} from "./coverageDrilldownConfig.js";
import {
	createEmptyRecord,
	deleteCoverageRecord,
	fetchCoverageRecord,
	saveCoverageRecord,
} from "./coverageDrilldownApi.js";

const CoverageDrilldown = () => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const [searchParams] = useSearchParams();
	const userContext = useUserContext();
	const user = userContext?.getUser?.() || null;

	const tableKey = searchParams.get("table")?.trim().toLowerCase() || "";
	const recordId = searchParams.get("id")?.trim() || "";
	const mode = searchParams.get("mode")?.trim().toLowerCase() || "edit";
	const addMode = isAddMode(mode);

	const tableConfig = useMemo(() => getCoverageTableConfig(tableKey), [tableKey]);

	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [record, setRecord] = useState(null);
	const [schema, setSchema] = useState({});
	const [formData, setFormData] = useState({});
	const [editedFormData, setEditedFormData] = useState({});
	const [tabs, setTabs] = useState(["Details"]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isDeleted, setIsDeleted] = useState(false);
	const [snack, setSnack] = useState({
		open: false,
		status: "success",
		message: "",
	});

	const isReadOnlyUser =
		!!user && typeof user.hasRole === "function" && user.hasRole("ngom-user");

	const copyToClipboard = (value) => {
		if (!value) return;
		navigator.clipboard.writeText(String(value));
		setSnack({
			open: true,
			status: "success",
			message: "Copied to clipboard",
		});
	};

	const loadRecord = useCallback(async () => {
		if (!tableConfig) {
			setLoadError("Unsupported or missing table parameter.");
			setIsLoading(false);
			return;
		}

		if (!addMode && !recordId) {
			setLoadError("Record id is required for edit mode.");
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setLoadError("");
		setIsDeleted(false);

		try {
			if (addMode) {
				const empty = createEmptyRecord(tableKey);
				setRecord(empty.record);
				setSchema(empty.schema);
				setFormData(empty.formData);
				setEditedFormData({});
				setTabs(["Details"]);
			} else {
				const result = await fetchCoverageRecord(tableKey, recordId);
				setRecord(result.record);
				setSchema(result.schema);
				setFormData(result.formData);
				setEditedFormData({});
				setTabs(["Details"]);
			}
		} catch (error) {
			setLoadError(error?.message || "Unable to load record.");
			setRecord(null);
		} finally {
			setIsLoading(false);
		}
	}, [addMode, recordId, tableConfig, tableKey]);

	useEffect(() => {
		loadRecord();
	}, [loadRecord]);

	const handleFormChange = (data) => {
		setEditedFormData(data);
	};

	const buildPayload = () => {
		const merged = { ...formData, ...editedFormData };
		return {
			...record,
			...merged,
			id: record?.id || recordId || null,
		};
	};

	const handleSave = async () => {
		if (!tableConfig || isReadOnlyUser) return;

		setIsSaving(true);
		try {
			const payload = buildPayload();
			await saveCoverageRecord(tableKey, payload);
			setFormData({ ...formData, ...editedFormData });
			setEditedFormData({});
			setSnack({
				open: true,
				status: "success",
				message: addMode
					? "Record saved successfully."
					: "Changes saved successfully.",
			});
		} catch (error) {
			setSnack({
				open: true,
				status: "error",
				message: error?.message || "Unable to save record.",
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!tableConfig || isReadOnlyUser || addMode) return;

		setDeleteDialogOpen(false);
		setIsDeleting(true);

		try {
			await deleteCoverageRecord(tableKey, recordId);
			setIsDeleted(true);
			setSnack({
				open: true,
				status: "success",
				message: "Record deleted successfully.",
			});
		} catch (error) {
			setSnack({
				open: true,
				status: "error",
				message: error?.message || "Unable to delete record.",
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const handleGoBack = () => {
		if (window.history.length > 1) {
			window.history.back();
			return;
		}
		window.close();
	};

	const listTypeLabel =
		tableConfig?.listType === COVERAGE_LIST_TYPES.WHITELIST
			? "Whitelist"
			: "Blacklist";

	const listTypeColor =
		tableConfig?.listType === COVERAGE_LIST_TYPES.WHITELIST
			? colors.greenAccent[500]
			: colors.redAccent[500];

	const pageTitle = tableConfig
		? addMode
			? `Add ${tableConfig.label}`
			: `Edit ${tableConfig.label}`
		: "Coverage list editor";

	const pageSubtitle = addMode
		? "Create a new coverage list entry"
		: "Modify or remove an existing coverage list entry";

	const hasPendingChanges = Object.keys(editedFormData).length > 0;
	const formDisabled = isDeleted || isSaving || isDeleting;

	if (!tableConfig && !isLoading) {
		return (
			<Box m="20px">
				<Header title="Coverage list editor" subtitle="Invalid request" />
				<Alert severity="error" sx={{ maxWidth: 720 }}>
					Unsupported table &quot;{tableKey || "—"}&quot;. Open this page from
					the Grafana dashboard using a valid table parameter.
				</Alert>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100%",
				backgroundColor: colors.primary[500],
			}}
		>
			<Box m="20px" sx={{ maxWidth: 1200 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 2,
						mb: 1,
					}}
				>
					<Header title={pageTitle} subtitle={pageSubtitle} />
					<Tooltip title="Return to previous page">
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<ArrowBackIcon />}
							onClick={handleGoBack}
							sx={{ mt: 1, flexShrink: 0 }}
						>
							Back
						</Button>
					</Tooltip>
				</Box>

				{isLoading && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 320,
						}}
					>
						<CircularProgress color="secondary" />
					</Box>
				)}

				{!isLoading && loadError && (
					<Alert severity="error" sx={{ maxWidth: 720 }}>
						{loadError}
					</Alert>
				)}

				{!isLoading && !loadError && record && (
					<Paper
						elevation={3}
						sx={{
							backgroundColor: colors.primary[400],
							border: `1px solid ${colors.grey[700]}`,
							overflow: "hidden",
						}}
					>
						<Box sx={{ px: 3, py: 2.5 }}>
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1,
									mb: 2,
								}}
							>
								<Chip
									label={listTypeLabel}
									sx={{
										backgroundColor: listTypeColor,
										color: "#fff",
										fontWeight: 600,
									}}
								/>
								<Chip
									label={tableConfig.label}
									variant="outlined"
									sx={{
										borderColor: colors.grey[500],
										color: colors.grey[100],
									}}
								/>
								{!addMode && record.id && (
									<Chip
										label={`ID: ${record.id}`}
										sx={{
											maxWidth: "100%",
											color: colors.grey[100],
											borderColor: colors.grey[600],
										}}
										variant="outlined"
										onClick={() => copyToClipboard(record.id)}
									/>
								)}
								{addMode && (
									<Chip
										label="New record"
										color="secondary"
										variant="outlined"
									/>
								)}
								{isDeleted && (
									<Chip label="Deleted" color="error" variant="filled" />
								)}
							</Box>

							{hasPendingChanges && !isDeleted && (
								<Alert severity="info" sx={{ mb: 2 }}>
									You have unsaved changes.
								</Alert>
							)}

							{isDeleted && (
								<Alert severity="success" sx={{ mb: 2 }}>
									This record has been deleted. You can close this page and
									return to the dashboard.
								</Alert>
							)}

							<Divider sx={{ mb: 2, borderColor: colors.grey[700] }} />

							<JsonSchemaBox
								schema={schema}
								formData={formData}
								onChange={handleFormChange}
								tabs={tabs}
								spacing={2}
								padding={1}
							/>
						</Box>

						<Divider sx={{ borderColor: colors.grey[700] }} />

						<Box
							sx={{
								px: 3,
								py: 2,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
								backgroundColor: colors.primary[600],
							}}
						>
							<Typography variant="body2" color={colors.grey[300]}>
								{isReadOnlyUser
									? "You do not have permission to modify coverage lists."
									: "Changes are saved directly to the coverage database."}
							</Typography>

							<Grid container spacing={1.5} justifyContent="flex-end" sx={{ width: "auto" }}>
								{!addMode && (
									<Grid item>
										<Button
											variant="outlined"
											color="error"
											startIcon={<DeleteOutlineIcon />}
											onClick={() => setDeleteDialogOpen(true)}
											disabled={
												formDisabled || isReadOnlyUser || isDeleted
											}
										>
											Delete
										</Button>
									</Grid>
								)}
								<Grid item>
									<Button
										variant="contained"
										color="secondary"
										startIcon={<SaveOutlinedIcon />}
										onClick={handleSave}
										disabled={
											formDisabled || isReadOnlyUser || isDeleted
										}
									>
										{isSaving ? "Saving…" : "Save"}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Paper>
				)}
			</Box>

			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle fontWeight="bold">Delete record</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: colors.grey[100] }}>
						This will permanently remove the selected entry from{" "}
						<strong>{tableConfig?.label}</strong>. This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteDialogOpen(false)}
						variant="contained"
						color="secondary"
					>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						variant="contained"
						color="error"
						autoFocus
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snack.open}
				autoHideDuration={5000}
				onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
					severity={snack.status}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{snack.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default CoverageDrilldown;
